"use client";

import { c as _c } from "react/compiler-runtime";
import { track } from "@opensea/analytics";
import { graphql, useQuery } from "@opensea/graphql";
import { Button } from "@opensea/next-ui-kit";
import { AnimatedNumber, classNames, FlexCenter, SideSheet, SkeletonBlock, TextBody } from "@opensea/ui-kit";
import { Notifications as NotificationsIcon } from "@opensea/ui-kit/icons";
import { Suspense, useState } from "react";
import { useTopNavbarSkeletonClassNames } from "@/components/navigation/top-nav/hooks";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { Sound, usePlaySound } from "@/hooks/use-play-sound";
import { useSyncState } from "@/hooks/use-sync-state";
import { useAddress } from "@/hooks/wallet";
import { PAGE_SIZE } from "./constants";
import { NotificationsContext } from "./NotificationsContext";
import { NotificationsSheet } from "./NotificationsSheet";
import { useNotificationSubscription } from "./use-notification.subscription";
const query = graphql(`
  query NotificationsQuery($address: Address!) {
    unreadNotificationsCount(address: $address)
  }
`);
function NotificationsBadgeSkeleton() {
  const $ = _c(4);
  const skeletonClassNames = useTopNavbarSkeletonClassNames();
  let t0;
  if ($[0] !== skeletonClassNames) {
    t0 = classNames(skeletonClassNames, "size-5 rounded-full");
    $[0] = skeletonClassNames;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <FlexCenter className="md:px-4"><SkeletonBlock className={t0} /></FlexCenter>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function NotificationsBadge(t0) {
  const $ = _c(29);
  const {
    iconSize: t1,
    showCount: t2
  } = t0;
  const iconSize = t1 === undefined ? 20 : t1;
  const showCount = t2 === undefined ? true : t2;
  const address = useAddress();
  const [playNotification] = usePlaySound(Sound.Notification);
  const t3 = address ?? "";
  let t4;
  if ($[0] !== t3) {
    t4 = {
      address: t3
    };
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = !address;
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = {
      query,
      variables: t4,
      pause: t5
    };
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const [t7] = useQuery(t6);
  const {
    data
  } = t7;
  const [count, setCount] = useSyncState(data?.unreadNotificationsCount ?? 0);
  const [isOpen, setIsOpen] = useState(false);
  const hasUnread = count > 0;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = open => {
      setIsOpen(open);
      if (open) {
        track("Open Notifications Sheet");
      } else {
        track("Close Notifications Sheet");
      }
    };
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const openNotificationsSheet = useAuthenticatedCallback(t8);
  let t9;
  if ($[6] !== playNotification || $[7] !== setCount) {
    t9 = newUnread => {
      setCount(prev => prev + newUnread);
      if (newUnread > 0) {
        playNotification();
      }
    };
    $[6] = playNotification;
    $[7] = setCount;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== address || $[10] !== t9) {
    t10 = {
      address,
      setItems: _temp,
      incrementCount: t9
    };
    $[9] = address;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  useNotificationSubscription(t10);
  if (!address) {
    return null;
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      handleNotificationClick: () => setIsOpen(false)
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== setCount) {
    t12 = <NotificationsSheet setUnreadCount={setCount} />;
    $[13] = setCount;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== iconSize) {
    t13 = <NotificationsIcon size={iconSize} />;
    $[15] = iconSize;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== count || $[18] !== hasUnread || $[19] !== showCount) {
    t14 = hasUnread && showCount ? <TextBody className="font-mono" size="sm">{count >= PAGE_SIZE ? `${String(PAGE_SIZE)}+` : <AnimatedNumber value={count} />}</TextBody> : null;
    $[17] = count;
    $[18] = hasUnread;
    $[19] = showCount;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = <Button className="md:px-2.5 px-0" variant="ghost"><FlexCenter className="gap-2">{t13}{t14}</FlexCenter></Button>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isOpen || $[25] !== openNotificationsSheet || $[26] !== t12 || $[27] !== t15) {
    t16 = <NotificationsContext.Provider value={t11}><SideSheet content={t12} direction="right" onOpenChange={openNotificationsSheet} open={isOpen}>{t15}</SideSheet></NotificationsContext.Provider>;
    $[24] = isOpen;
    $[25] = openNotificationsSheet;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
}
function _temp() {
  return null;
}
export function Notifications(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NotificationsBadgeSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><NotificationsBadge {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}